import * as React from 'react';

import { Check, ChevronsUpDown, X } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/TailwindComponents/base/Command/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/TailwindComponents/base/Tooltip/Tooltip';
import { SelectGroupType } from '@/components/TailwindComponents/components/Form/types/select';
import { cn } from '@/components/TailwindComponents/lib/utils';

export interface ComboboxProps {
  options?: {
    value: string;
    label: string;
  }[];
  groups?: SelectGroupType[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyResultsMessage?: string;
  className?: string;
  onSelect?: (value: string) => void;
  value?: string | null;
  disabled?: boolean;
  /**
   * @descriptionIf true, shows a tooltip with the full selected value. Requires TooltipProvider to be present in the component tree.
   */
  withTooltip?: boolean;
  error?: string;
  /**
   * @description If true, shows a clear (X) button when a value is selected.
   */
  clearable?: boolean;
  /**
   * Width of the combobox
   */
  width?: 'auto' | 'full' | 'fit' | 'sm' | 'md' | 'lg' | 'xl' | string;
  /**
   * @description If true, enables search functionality in the dropdown. If false, hides the search input.
   */
  searchable?: boolean;
  /**
   * @description DOM element to portal the dropdown into. Useful when rendered inside a modal/sheet
   * to keep the dropdown within the modal's DOM tree and avoid pointer-event blocking.
   */
  portalContainer?: HTMLElement | null;
}

// TODO: implement groups
export function Combobox({
  className,
  options,
  // for now this will be empty
  // ts-ignore
  groups,
  placeholder,
  searchPlaceholder,
  emptyResultsMessage,
  onSelect,
  value: initialValue,
  disabled = false,
  withTooltip = false,
  error,
  clearable = false,
  width = 'full',
  searchable = true,
  portalContainer,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(initialValue ?? '');
  const [search, setSearch] = React.useState('');

  // Width configuration
  const getWidthClass = () => {
    if (
      typeof width === 'string' &&
      !['auto', 'full', 'fit', 'sm', 'md', 'lg', 'xl'].includes(width)
    ) {
      return width; // Custom width
    }

    const widthMap = {
      auto: 'w-auto',
      full: 'w-full',
      fit: 'w-fit',
      sm: 'w-32',
      md: 'w-48',
      lg: 'w-64',
      xl: 'w-80',
    };

    return widthMap[width as keyof typeof widthMap] || 'w-full';
  };

  // Sync internal state with parent value when it changes programmatically
  React.useEffect(() => {
    const parentValue = initialValue ?? '';
    if (value !== parentValue) {
      setValue(parentValue);
    }
  }, [initialValue]);

  // Prevent opening if disabled
  const handleOpenChange = (nextOpen: boolean) => {
    if (!disabled) setOpen(nextOpen);
  };

  const filteredOptions = React.useMemo(
    () =>
      !searchable || !search
        ? options
        : options?.filter((option) =>
            option.label.toLowerCase().includes(search.toLowerCase()),
          ),
    [options, search, searchable],
  );

  if (groups && groups.length > 0) {
    // gorups to options
    options = groups?.flatMap((group) => group.options) || [];
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            'justify-between',
            getWidthClass(),
            disabled && 'cursor-not-allowed !pointer-events-auto',
            className,
          )}
          type="button"
        >
          {withTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-1 text-left truncate cursor-default">
                  {value
                    ? (options ?? []).find((option) => option.value === value)
                        ?.label
                    : placeholder || 'Select option...'}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {value
                  ? (options ?? []).find((option) => option.value === value)
                      ?.label
                  : placeholder || 'Select option...'}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="flex-1 text-left truncate">
              {value
                ? (options ?? []).find((option) => option.value === value)
                    ?.label
                : placeholder || 'Select option...'}
            </span>
          )}
          {clearable && !!value && !disabled && (
            <span
              className="flex items-center ml-2 cursor-pointer hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                setValue('');
                setOpen(false);
                onSelect?.('');
              }}
              tabIndex={0}
              role="button"
              aria-label="Clear selection"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setValue('');
                  setOpen(false);
                  onSelect?.('');
                }
              }}
            >
              <X className="w-4 h-4" />
            </span>
          )}
          <ChevronsUpDown className="flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <div className="text-red-500 text-[14px] font-[400]">{error}</div>
      <PopoverContent
        className={cn(
          'p-0 z-[1201] w-[var(--radix-popover-trigger-width)]',
          disabled && 'opacity-60 pointer-events-none',
        )}
        container={portalContainer}
      >
        <Command
          /**
           * solution for search: https://github.com/shadcn-ui/ui/issues/458
           */
          filter={
            searchable
              ? (optionValue, searchTerm, keywords) => {
                  const extendValue = optionValue + ' ' + keywords?.join(' ');
                  if (
                    extendValue.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                    return 1;
                  return 0;
                }
              : undefined
          }
        >
          {searchable && (
            <CommandInput
              placeholder={searchPlaceholder || 'Search list...'}
              className="w-full h-9"
              disabled={disabled}
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList>
            <CommandEmpty className="w-full py-6 text-center">
              {emptyResultsMessage || 'No results found.'}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions?.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value + ' ' + option.label}
                  keywords={[option.label]}
                  onSelect={(currentValue) => {
                    if (disabled) return;
                    // get the value - the first part of the string
                    const selectedValue = currentValue.split(' ')[0];
                    setValue(selectedValue === value ? '' : selectedValue);
                    setOpen(false);
                    onSelect?.(selectedValue === value ? '' : selectedValue);
                  }}
                  data-disabled={disabled}
                >
                  {option.label}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
